import React, { useMemo, useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';

const PX_PER_CM = 37.7953;
const RULER_SIZE = 22;
const HANDLE_WIDTH = 6;
const MIN_MARGIN_PX = 20;

interface HorizontalRulerProps {
  pageWidthPx: number;
  marginLeftPx: number;
  marginRightPx: number;
  onMarginChange?: (side: 'left' | 'right', valuePx: number) => void;
}

export const HorizontalRuler: React.FC<HorizontalRulerProps> = ({
  pageWidthPx, marginLeftPx, marginRightPx, onMarginChange
}) => {
  const totalCm = pageWidthPx / PX_PER_CM;
  const rulerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<'left' | 'right' | null>(null);
  const [tempLeft, setTempLeft] = useState<number | null>(null);
  const [tempRight, setTempRight] = useState<number | null>(null);

  const currentLeft = tempLeft ?? marginLeftPx;
  const currentRight = tempRight ?? marginRightPx;

  const handleMouseDown = useCallback((side: 'left' | 'right') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(side);

    const startX = e.clientX;
    const startVal = side === 'left' ? currentLeft : currentRight;

    const handleMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      const newVal = side === 'left'
        ? Math.max(MIN_MARGIN_PX, Math.min(pageWidthPx / 2, startVal + delta))
        : Math.max(MIN_MARGIN_PX, Math.min(pageWidthPx / 2, startVal - delta));

      if (side === 'left') setTempLeft(Math.round(newVal));
      else setTempRight(Math.round(newVal));
    };

    const handleMouseUp = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      const finalVal = side === 'left'
        ? Math.max(MIN_MARGIN_PX, Math.min(pageWidthPx / 2, startVal + delta))
        : Math.max(MIN_MARGIN_PX, Math.min(pageWidthPx / 2, startVal - delta));

      setDragging(null);
      setTempLeft(null);
      setTempRight(null);
      onMarginChange?.(side, Math.round(finalVal));
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [currentLeft, currentRight, pageWidthPx, onMarginChange]);

  const marks = useMemo(() => {
    const items: React.ReactNode[] = [];
    const maxCm = Math.ceil(totalCm);
    for (let cm = 0; cm <= maxCm; cm++) {
      const pct = (cm / totalCm) * 100;
      if (pct > 100) break;
      items.push(
        <Box key={`cm-${cm}`} sx={{
          position: 'absolute', left: `${pct}%`,
          height: '12px', width: '1px',
          backgroundColor: '#5f6368', bottom: 0,
          '&::before': cm > 0 ? {
            content: `"${cm}"`, position: 'absolute',
            fontSize: '7px', color: '#5f6368',
            fontFamily: 'Arial, sans-serif',
            bottom: '12px', left: '-3px',
          } : undefined,
        }} />
      );
      for (let mm = 1; mm < 10; mm++) {
        const subPct = ((cm + mm / 10) / totalCm) * 100;
        if (subPct > 100) break;
        items.push(
          <Box key={`mm-${cm}-${mm}`} sx={{
            position: 'absolute', left: `${subPct}%`,
            height: mm === 5 ? '8px' : '4px',
            width: '1px', backgroundColor: '#9aa0a6', bottom: 0,
          }} />
        );
      }
    }
    return items;
  }, [totalCm]);

  const leftPct = (currentLeft / pageWidthPx) * 100;
  const rightPct = (currentRight / pageWidthPx) * 100;

  const handleStyle = {
    position: 'absolute' as const,
    top: 0,
    width: `${HANDLE_WIDTH}px`,
    height: '100%',
    cursor: 'col-resize',
    zIndex: 2,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '25%',
      height: '50%',
      width: '2px',
      backgroundColor: dragging ? '#1a73e8' : '#4285f4',
      left: '50%',
      transform: 'translateX(-50%)',
      borderRadius: '1px',
    },
    '&:hover::after': {
      backgroundColor: '#1a73e8',
      width: '3px',
    },
  };

  return (
    <Box ref={rulerRef} sx={{
      position: 'relative',
      width: `${pageWidthPx}px`,
      maxWidth: '100%',
      height: `${RULER_SIZE}px`,
      margin: '0 auto',
      background: '#f8f9fa',
      userSelect: dragging ? 'none' : 'auto',
      overflow: 'visible',
      flexShrink: 0,
    }}>
      <Box sx={{
        position: 'absolute', top: 0, left: 0,
        width: `${leftPct}%`, height: '100%',
        backgroundColor: 'rgba(0,0,0,0.04)',
      }} />
      <Box sx={{
        position: 'absolute', top: 0, right: 0,
        width: `${rightPct}%`, height: '100%',
        backgroundColor: 'rgba(0,0,0,0.04)',
      }} />

      {/* Draggable left margin handle */}
      <Box
        onMouseDown={handleMouseDown('left')}
        sx={{ ...handleStyle, left: `calc(${leftPct}% - ${HANDLE_WIDTH / 2}px)` }}
      />
      {/* Draggable right margin handle */}
      <Box
        onMouseDown={handleMouseDown('right')}
        sx={{ ...handleStyle, right: `calc(${rightPct}% - ${HANDLE_WIDTH / 2}px)` }}
      />

      {marks}
    </Box>
  );
};

interface VerticalRulerProps {
  marginTopPx: number;
  marginBottomPx: number;
  pageHeightPx: number;
  onMarginChange?: (side: 'top' | 'bottom', valuePx: number) => void;
}

export const VerticalRuler: React.FC<VerticalRulerProps> = ({
  marginTopPx, marginBottomPx, pageHeightPx, onMarginChange
}) => {
  const totalCm = pageHeightPx / PX_PER_CM;
  const [dragging, setDragging] = useState<'top' | 'bottom' | null>(null);
  const [tempTop, setTempTop] = useState<number | null>(null);
  const [tempBottom, setTempBottom] = useState<number | null>(null);

  const currentTop = tempTop ?? marginTopPx;
  const currentBottom = tempBottom ?? marginBottomPx;

  const handleMouseDown = useCallback((side: 'top' | 'bottom') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(side);

    const startY = e.clientY;
    const startVal = side === 'top' ? currentTop : currentBottom;

    const handleMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientY - startY;
      const newVal = side === 'top'
        ? Math.max(MIN_MARGIN_PX, Math.min(pageHeightPx / 2, startVal + delta))
        : Math.max(MIN_MARGIN_PX, Math.min(pageHeightPx / 2, startVal - delta));
      if (side === 'top') setTempTop(Math.round(newVal));
      else setTempBottom(Math.round(newVal));
    };

    const handleMouseUp = (ev: MouseEvent) => {
      const delta = ev.clientY - startY;
      const finalVal = side === 'top'
        ? Math.max(MIN_MARGIN_PX, Math.min(pageHeightPx / 2, startVal + delta))
        : Math.max(MIN_MARGIN_PX, Math.min(pageHeightPx / 2, startVal - delta));
      setDragging(null);
      setTempTop(null);
      setTempBottom(null);
      onMarginChange?.(side, Math.round(finalVal));
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [currentTop, currentBottom, pageHeightPx, onMarginChange]);

  const marks = useMemo(() => {
    const items: React.ReactNode[] = [];
    const maxCm = Math.ceil(totalCm);
    for (let cm = 0; cm <= maxCm; cm++) {
      const pct = (cm / totalCm) * 100;
      if (pct > 100) break;
      items.push(
        <Box key={`vcm-${cm}`} sx={{
          position: 'absolute', top: `${pct}%`,
          width: '12px', height: '1px',
          backgroundColor: '#5f6368', right: 0,
          '&::before': cm > 0 ? {
            content: `"${cm}"`, position: 'absolute',
            fontSize: '7px', color: '#5f6368',
            fontFamily: 'Arial, sans-serif',
            right: '14px', top: '-5px',
          } : undefined,
        }} />
      );
      for (let mm = 1; mm < 10; mm++) {
        const subPct = ((cm + mm / 10) / totalCm) * 100;
        if (subPct > 100) break;
        items.push(
          <Box key={`vmm-${cm}-${mm}`} sx={{
            position: 'absolute', top: `${subPct}%`,
            width: mm === 5 ? '8px' : '4px',
            height: '1px', backgroundColor: '#9aa0a6', right: 0,
          }} />
        );
      }
    }
    return items;
  }, [totalCm]);

  const topPct = (currentTop / pageHeightPx) * 100;
  const bottomPct = (currentBottom / pageHeightPx) * 100;

  const handleStyle = {
    position: 'absolute' as const,
    left: 0,
    height: `${HANDLE_WIDTH}px`,
    width: '100%',
    cursor: 'row-resize',
    zIndex: 2,
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '25%',
      width: '50%',
      height: '2px',
      backgroundColor: dragging ? '#1a73e8' : '#4285f4',
      top: '50%',
      transform: 'translateY(-50%)',
      borderRadius: '1px',
    },
    '&:hover::after': {
      backgroundColor: '#1a73e8',
      height: '3px',
    },
  };

  return (
    <Box sx={{
      position: 'sticky',
      top: 0, left: 0,
      width: `${RULER_SIZE}px`,
      height: `${pageHeightPx}px`,
      background: '#f8f9fa',
      borderRight: '1px solid #e0e0e0',
      userSelect: dragging ? 'none' : 'auto',
      overflow: 'visible',
      flexShrink: 0,
      zIndex: 5,
    }}>
      <Box sx={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: `${topPct}%`,
        backgroundColor: 'rgba(0,0,0,0.04)',
      }} />
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0,
        width: '100%', height: `${bottomPct}%`,
        backgroundColor: 'rgba(0,0,0,0.04)',
      }} />

      <Box
        onMouseDown={handleMouseDown('top')}
        sx={{ ...handleStyle, top: `calc(${topPct}% - ${HANDLE_WIDTH / 2}px)` }}
      />
      <Box
        onMouseDown={handleMouseDown('bottom')}
        sx={{ ...handleStyle, bottom: `calc(${bottomPct}% - ${HANDLE_WIDTH / 2}px)` }}
      />

      {marks}
    </Box>
  );
};

export const RULER_WIDTH = RULER_SIZE;

const Ruler = HorizontalRuler;
export default Ruler;
